// client/src/pages/ExamPlayer/index.jsx
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState, useAuth } from '../../context/AuthContext';
import customFetch from '../../api/customFetch';

const LoadingScreen = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: 'var(--bg)',
    color: 'var(--text)'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>טוען...</div>
      <div style={{ color: 'var(--text)' }}>מכין את המבחן</div>
    </div>
  </div>
);

export default function ExamPlayer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, user, loading } = useAuthState();
  const { logout } = useAuth();
  const timerRef = useRef(null);
  
  const [exam, setExam] = useState(null);
  const [resultId, setResultId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [examStarted, setExamStarted] = useState(false);
  const [examLoading, setExamLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    
    if (!isLoggedIn) {
      navigate('/user/login');
      return;
    }

    loadExam();
  }, [slug, isLoggedIn, loading]);

  useEffect(() => {
    // טיימר
    if (timeLeft > 0 && examStarted && !submitting) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(time => {
          if (time <= 1) {
            handleSubmit('timeout');
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [timeLeft, examStarted, submitting]);

  const loadExam = async () => {
    try {
      setExamLoading(true);
      const { data } = await customFetch.get(`/exams/user/${slug}`);
      
      if (data.success) {
        setExam(data.exam);
        if (data.exam.timeLimit) {
          setTimeLeft(data.exam.timeLimit * 60); // המרה לשניות
        }
      } else {
        setError(data.message || 'שגיאה בטעינת המבחן');
      }
    } catch (error) {
      console.error('Error loading exam:', error);
      if (error.response?.status === 401) {
        await logout();
        navigate('/user/login');
      } else if (error.response?.status === 403) {
        setError('אין לך הרשאה לגשת למבחן זה או שהגעת למספר הניסיונות המקסימלי');
      } else {
        setError('שגיאה בטעינת המבחן');
      }
    } finally {
      setExamLoading(false);
    }
  };

  const startExam = async () => {
    try {
      const { data } = await customFetch.post(`/exams/${slug}/start`);
      
      if (data.success) {
        setResultId(data.resultId);
        setExamStarted(true);
      } else {
        setError(data.message || 'שגיאה בהתחלת המבחן');
      }
    } catch (error) {
      console.error('Error starting exam:', error);
      setError('שגיאה בהתחלת המבחן');
    }
  };

  const saveAnswer = async (questionIndex, selectedAnswer) => {
    if (!resultId) return;

    try {
      await customFetch.post(`/exams/results/${resultId}/answer`, {
        questionIndex,
        selectedAnswer,
        timeSpent: 5 // זמן דמה
      });
      
      setAnswers(prev => ({
        ...prev,
        [questionIndex]: selectedAnswer
      }));
    } catch (error) {
      console.error('Error saving answer:', error);
    }
  };

  const handleSubmit = async (submissionType = 'manual') => {
    if (!resultId || submitting) return;

    try {
      setSubmitting(true);
      const { data } = await customFetch.post(`/exams/results/${resultId}/submit`, {
        submissionType
      });

      if (data.success) {
        navigate(`/exams/${slug}/results`, {
          state: { results: data.results }
        });
      } else {
        setError('שגיאה בשליחת המבחן');
      }
    } catch (error) {
      console.error('Error submitting exam:', error);
      setError('שגיאה בשליחת המבחן');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    if (!exam) return 0;
    const answeredCount = Object.keys(answers).length;
    return Math.round((answeredCount / exam.questions.length) * 100);
  };

  if (loading || examLoading) {
    return <LoadingScreen />;
  }

  if (!isLoggedIn) {
    return null;
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg)',
        direction: 'rtl'
      }}>
        <div style={{
          background: 'var(--card)',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          textAlign: 'center',
          maxWidth: '500px',
          width: '90%'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '20px' }}>❌</div>
          <h2 style={{ color: '#ffffff', marginBottom: '16px' }}>שגיאה</h2>
          <p style={{ color: 'var(--text)', marginBottom: '20px' }}>
            {error}
          </p>
          <button 
            onClick={() => navigate('/user/dashboard')}
            className="btn"
          >
            חזור לדשבורד
          </button>
        </div>
      </div>
    );
  }

  if (!exam) {
    return <LoadingScreen />;
  }

  // מסך הכנה למבחן
  if (!examStarted) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg)',
        direction: 'rtl'
      }}>
        <div style={{
          background: 'var(--card)',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          maxWidth: '600px',
          width: '90%'
        }}>
          <h1 style={{ textAlign: 'center', marginBottom: '30px', color: '#ffffff' }}>
            {exam.title}
          </h1>
          
          {exam.description && (
            <p style={{ color: 'var(--text)', marginBottom: '20px', lineHeight: '1.6' }}>
              {exam.description}
            </p>
          )}

          <div style={{
            background: 'var(--bg)',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '30px'
          }}>
            <h3 style={{ marginTop: 0, color: '#ffffff' }}>פרטי המבחן:</h3>
            <ul style={{ color: 'var(--text)', lineHeight: '1.8' }}>
              <li>מספר שאלות: {exam.questions?.length || 0}</li>
              <li>ציון עובר: {exam.passScore}%</li>
              {exam.timeLimit && (
                <li>זמן מוגבל: {exam.timeLimit} דקות</li>
              )}
              <li>מספר ניסיונות מקסימלי: {exam.maxAttempts}</li>
            </ul>
          </div>

          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            color: '#856404',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <strong>הוראות חשובות:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingRight: '20px' }}>
              <li>ודא שיש לך חיבור אינטרנט יציב</li>
              <li>התשובות נשמרות אוטומטית</li>
              {exam.timeLimit && (
                <li>המבחן יסתיים אוטומטית כשהזמן יגמר</li>
              )}
              <li>לא ניתן לחזור לשאלות קודמות לאחר המעבר</li>
            </ul>
          </div>

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button
              className="btn"
              onClick={startExam}
              style={{
                background: 'var(--accent)',
                color: '#0b1c2b',
                padding: '16px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}
            >
              התחל מבחן
            </button>
            
            <button
              onClick={() => navigate('/user/dashboard')}
              style={{
                background: 'var(--text)',
                color: 'var(--bg)',
                border: 'none',
                padding: '16px 32px',
                borderRadius: '16px',
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}
            >
              חזור לדשבורד
            </button>
          </div>
        </div>
      </div>
    );
  }

  // מסך המבחן
  const currentQ = exam.questions[currentQuestion];
  const isLastQuestion = currentQuestion === exam.questions.length - 1;
  const canProceed = answers[currentQuestion];

  return (
    <div className="page" dir="rtl" style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Header עם מידע על המבחן */}
      <div style={{
        background: 'var(--card)',
        padding: '20px',
        borderRadius: '16px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#ffffff' }}>{exam.title}</h2>
          <div style={{ color: 'var(--text)', fontSize: '0.9rem' }}>
            שאלה {currentQuestion + 1} מתוך {exam.questions.length}
          </div>
        </div>
        
        <div style={{ textAlign: 'left' }}>
          {timeLeft !== null && (
            <div style={{ 
              fontSize: '1.2rem', 
              fontWeight: 'bold',
              color: timeLeft < 300 ? '#e74c3c' : 'var(--accent)' // אדום אם נותרו פחות מ-5 דקות
            }}>
              {formatTime(timeLeft)}
            </div>
          )}
          <div style={{ color: 'var(--text)', fontSize: '0.8rem' }}>
            הושלם: {getProgressPercentage()}%
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{
        width: '100%',
        height: '8px',
        background: 'var(--text)',
        borderRadius: '4px',
        marginBottom: '20px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          background: 'var(--accent)',
          width: `${((currentQuestion + 1) / exam.questions.length) * 100}%`,
          transition: 'width 0.3s ease'
        }} />
      </div>

      {/* השאלה */}
      <div className="card" style={{ padding: '30px' }}>
        <h3 style={{ 
          marginBottom: '20px', 
          lineHeight: '1.6',
          color: '#ffffff',
          fontSize: '1.2rem'
        }}>
          {currentQ.question}
        </h3>

        <div style={{ display: 'grid', gap: '12px' }}>
          {currentQ.options.map((option) => (
            <button
              key={option.label}
              onClick={() => saveAnswer(currentQuestion, option.label)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '16px 20px',
                borderRadius: '12px',
                border: answers[currentQuestion] === option.label 
                  ? '2px solid var(--accent)' 
                  : '1px solid var(--text)',
                background: answers[currentQuestion] === option.label 
                  ? 'rgba(187, 233, 243, 0.1)' 
                  : 'var(--bg)',
                color: '#ffffff',
                cursor: 'pointer',
                textAlign: 'right',
                transition: 'all 0.2s ease',
                fontSize: '1rem'
              }}
            >
              <div style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: answers[currentQuestion] === option.label 
                  ? 'var(--accent)' 
                  : 'transparent',
                border: '2px solid var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: answers[currentQuestion] === option.label 
                  ? '#0b1c2b' 
                  : 'var(--accent)',
                fontWeight: 'bold'
              }}>
                {option.label}
              </div>
              <span style={{ flex: 1 }}>{option.text}</span>
            </button>
          ))}
        </div>
      </div>

      {/* כפתורי ניווט */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '30px',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate('/user/dashboard')}
          style={{
            background: '#e74c3c',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '16px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          יציאה מהמבחן
        </button>

        <div style={{ display: 'flex', gap: '12px' }}>
          {currentQuestion > 0 && (
            <button
              onClick={() => setCurrentQuestion(curr => curr - 1)}
              style={{
                background: 'var(--text)',
                color: 'var(--bg)',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '16px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              שאלה קודמת
            </button>
          )}

          {!isLastQuestion ? (
            <button
              className="btn"
              onClick={() => setCurrentQuestion(curr => curr + 1)}
              disabled={!canProceed}
              style={{
                opacity: canProceed ? 1 : 0.6,
                cursor: canProceed ? 'pointer' : 'not-allowed'
              }}
            >
              שאלה הבאה
            </button>
          ) : (
            <button
              className="btn"
              onClick={() => handleSubmit('manual')}
              disabled={!canProceed || submitting}
              style={{
                background: '#27ae60',
                opacity: canProceed && !submitting ? 1 : 0.6,
                cursor: canProceed && !submitting ? 'pointer' : 'not-allowed'
              }}
            >
              {submitting ? 'שולח...' : 'סיום המבחן'}
            </button>
          )}
        </div>
      </div>

      {/* אזהרה אם הזמן עומד להיגמר */}
      {timeLeft !== null && timeLeft < 300 && timeLeft > 0 && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#e74c3c',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          fontSize: '1rem',
          fontWeight: 'bold',
          zIndex: 1000,
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
        }}>
          נותרו {Math.floor(timeLeft / 60)} דקות!
        </div>
      )}
    </div>
  );
}